import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUpRight,
  ExternalLink,
  Github,
  Linkedin,
  Play,
  RotateCcw,
  X,
} from 'lucide-react';
import projectsData from './projectsData.json';
import ContactForm from './ContactForm';

const PROFILE = {
  name: 'Dan Chernopolskii',
  role: 'Engineering Manager',
  status: 'Available for the right build',
  focus: 'Engineering leadership / Systems / Platforms',
};

const pad = (number) => String(number + 1).padStart(2, '0');

const INTERCEPTS = [
  {
    code: '01',
    type: 'pipe',
    system: 'PIPE CONNECT',
    title: 'Waterworks / medium route',
    prompt: 'Connect the intake to the field pump. Rotate every pipe until one unbroken route reaches OUT.',
    success: 'Flow established. The field pump is online.',
  },
  {
    code: '02',
    type: 'queue',
    system: 'CI / CD TRAIN',
    title: 'Release train / inverted',
    prompt: 'A release cannot outrun its commit, test suite, or signing step. Restore the pipeline order.',
    success: 'Release train sequenced. Deployment lane available.',
  },
  {
    code: '03',
    type: 'merge',
    system: 'MERGE QUEUE',
    title: 'Meld / three decisions',
    prompt: 'Keep the scheduling output correct. Merge or discard each of three code pieces against the stated result.',
    success: 'Three-way merge accepted. Schedule output remains correct.',
  },
  {
    code: '04',
    type: 'mechanic',
    system: 'THE MECHANIC',
    title: 'Wireframe / untangle',
    prompt: 'Drag the modules until no cable crosses another. Only intersecting cables are marked.',
    success: 'Cable layout cleared. Module graph is readable.',
  },
  {
    code: '05',
    type: 'terminal',
    system: 'NETWORK DIAGNOSTIC',
    title: 'Guest wi-fi / DNS missing',
    prompt: 'Get the workshop online. Gather evidence, inspect the lease, then identify why names cannot resolve.',
    success: 'DNS restored from DHCP lease. Workshop network reachable.',
  },
];

const PIPE_TILES = [
  { type: 'empty' }, { type: 'empty' }, { type: 'empty' }, { type: 'empty' },
  { type: 'empty' }, { type: 'empty' }, { type: 'corner', rotation: 0 }, { type: 'straight', rotation: 0 },
  { type: 'empty' }, { type: 'corner', rotation: 2 }, { type: 'corner', rotation: 1 }, { type: 'empty' },
  { type: 'straight', rotation: 0 }, { type: 'corner', rotation: 0 }, { type: 'empty' }, { type: 'empty' },
];
const PIPE_DELTAS = [[0, -1], [1, 0], [0, 1], [-1, 0]];
const PIPE_CONNECTIONS = { straight: [0, 2], corner: [0, 1] };
const QUEUE_TARGET = ['artifact', 'signer', 'scheduler', 'release'];
const QUEUE_LABELS = { artifact: 'COMMIT', signer: 'TEST SUITE', scheduler: 'SIGNING', release: 'DEPLOY' };
const MERGE_HUNKS = [
  { file: 'court-availability.ts', dependency: 'keep tier in the court id', target: 'branch', main: ['const lateId = location + "~~late";'], branch: ['const lateId = location + "~~late-" + tier;'] },
  { file: 'generate-service.ts', dependency: 'do not reorder primary courts', target: 'main', main: ['const court = primaryCourts[index];'], branch: ['const court = primaryCourts.sort()[index];'] },
  { file: 'persist-schedule.ts', dependency: 'render the tier-aware label', target: 'branch', main: ['court: "Court " + game.court'], branch: ['court: mergeCourtLabel(game, offsets)'] },
];
const MECHANIC_NODES = [
  { id: 'app', label: 'app.ts', x: 20, y: 22 }, { id: 'router', label: 'router.ts', x: 80, y: 22 },
  { id: 'session', label: 'session.ts', x: 20, y: 78 }, { id: 'auth', label: 'auth.ts', x: 80, y: 78 },
  { id: 'store', label: 'store.ts', x: 50, y: 30 }, { id: 'events', label: 'events.ts', x: 50, y: 70 },
];
const MECHANIC_EDGES = [
  ['app', 'auth'], ['router', 'session'], ['app', 'router'], ['session', 'auth'], ['store', 'events'], ['app', 'events'], ['router', 'store'],
];
const pipeConnections = (tile, rotation) => (PIPE_CONNECTIONS[tile.type] || []).map((direction) => (direction + rotation) % 4);

const isPipeRouteComplete = (rotations) => {
  const open = [12];
  const visited = new Set();
  while (open.length) {
    const index = open.shift();
    if (visited.has(index)) continue;
    visited.add(index);
    const tile = PIPE_TILES[index];
    for (const direction of pipeConnections(tile, rotations[index])) {
      if (index === 7 && direction === 1) return true;
      const x = index % 4;
      const y = Math.floor(index / 4);
      const [dx, dy] = PIPE_DELTAS[direction];
      const neighborX = x + dx;
      const neighborY = y + dy;
      if (neighborX < 0 || neighborX > 3 || neighborY < 0 || neighborY > 3) continue;
      const neighbor = neighborY * 4 + neighborX;
      const neighborDirections = pipeConnections(PIPE_TILES[neighbor], rotations[neighbor]);
      if (neighborDirections.includes((direction + 2) % 4)) open.push(neighbor);
    }
  }
  return false;
};

const freshPipeTurns = () => PIPE_TILES.map((tile) => tile.rotation || 0);
const freshQueueOrder = () => ['release', 'scheduler', 'artifact', 'signer'];
const freshMergeChoices = () => Array(MERGE_HUNKS.length).fill(null);
const freshMechanicNodes = () => MECHANIC_NODES.map((node) => ({ ...node }));
const side = (a, b, c) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
const intersectingWireIndexes = (nodes) => {
  const crossed = new Set();
  MECHANIC_EDGES.forEach(([a, b], index) => MECHANIC_EDGES.slice(index + 1).forEach(([c, d], offset) => {
    if ([a, b].includes(c) || [a, b].includes(d)) return false;
    const [p1, p2, p3, p4] = [a, b, c, d].map((id) => nodes.find((node) => node.id === id));
    if (side(p1, p2, p3) * side(p1, p2, p4) < 0 && side(p3, p4, p1) * side(p3, p4, p2) < 0) { crossed.add(index); crossed.add(index + offset + 1); }
  }));
  return crossed;
};
const hasIntersectingWires = (nodes) => intersectingWireIndexes(nodes).size > 0;

function SystemsIntercept({ onClose, projects, onRecoverProject }) {
  const [stageIndex, setStageIndex] = useState(0);
  const [queueOrder, setQueueOrder] = useState(freshQueueOrder);
  const [mergeChoices, setMergeChoices] = useState(freshMergeChoices);
  const [pipeTurns, setPipeTurns] = useState(freshPipeTurns);
  const [mechanicNodes, setMechanicNodes] = useState(freshMechanicNodes);
  const [draggedMechanicNode, setDraggedMechanicNode] = useState(null);
  const [terminalLog, setTerminalLog] = useState([]);
  const [recoveredProject, setRecoveredProject] = useState(null);
  const [message, setMessage] = useState('Rotate the pipes to connect the intake to the field pump.');
  const [isResolving, setIsResolving] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const timerRef = useRef(null);
  const mission = INTERCEPTS[stageIndex];
  const resetMessages = {
    queue: 'Release train reset. Restore the dependency order.',
    merge: 'Merge reset. Carry the tiered-court dependency chain into main.',
    pipe: 'Pipe grid reset. Connect IN to OUT.',
    mechanic: 'Wireframe reset. Untangle every cable.',
    terminal: 'Network terminal reset. Find why DNS does not resolve.',
  };
  const resetMessage = resetMessages[mission.type];

  const resetMissionState = () => {
    setQueueOrder(freshQueueOrder());
    setMergeChoices(freshMergeChoices());
    setPipeTurns(freshPipeTurns());
    setMechanicNodes(freshMechanicNodes());
    setDraggedMechanicNode(null);
    setTerminalLog([]);
  };

  useEffect(() => {
    closeRef.current?.focus();
    return () => window.clearTimeout(timerRef.current);
  }, []);

  const reset = () => {
    window.clearTimeout(timerRef.current);
    resetMissionState();
    setRecoveredProject(null);
    setMessage(resetMessage);
    setIsResolving(false);
    setIsComplete(false);
  };

  const advance = () => {
    if (stageIndex === INTERCEPTS.length - 1) {
      setIsComplete(true);
      setRecoveredProject(projects[Math.floor(Math.random() * projects.length)]);
      setMessage('All systems nominal. Thanks for taking the long way around.');
      return;
    }
    const nextMission = INTERCEPTS[stageIndex + 1];
    setStageIndex((current) => current + 1);
    resetMissionState();
    setIsResolving(false);
    setMessage(nextMission.type === 'merge' ? 'Meld conflict detected. Apply the feature side through every dependency.' : 'New system detected. Read the goal, then repair the fault.');
  };

  const resolveMission = (successMessage) => {
    setIsResolving(true);
    setMessage(successMessage);
    timerRef.current = window.setTimeout(advance, 1050);
  };

  const rotatePipe = (index) => {
    if (isResolving || isComplete || PIPE_TILES[index].type === 'empty') return;
    const nextTurns = pipeTurns.map((turn, turnIndex) => (turnIndex === index ? (turn + 1) % 4 : turn));
    setPipeTurns(nextTurns);
    if (isPipeRouteComplete(nextTurns)) {
      resolveMission(mission.success);
    } else {
      setMessage('Pipe rotated. The route has not reached OUT yet.');
    }
  };

  const moveQueueItem = (index, direction) => {
    if (isResolving || isComplete) return;
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= queueOrder.length) return;
    const nextOrder = [...queueOrder];
    [nextOrder[index], nextOrder[nextIndex]] = [nextOrder[nextIndex], nextOrder[index]];
    setQueueOrder(nextOrder);
    if (nextOrder.every((item, itemIndex) => item === QUEUE_TARGET[itemIndex])) resolveMission(mission.success);
    else setMessage(`${QUEUE_LABELS[nextOrder[nextIndex]]} repositioned. Dependencies are still unresolved.`);
  };

  const dragMechanicNode = (event, id) => {
    if (isResolving || isComplete) return;
    const board = event.currentTarget.parentElement;
    if (!board) return;
    const bounds = board.getBoundingClientRect();
    const x = Math.min(90, Math.max(10, ((event.clientX - bounds.left) / bounds.width) * 100));
    const y = Math.min(88, Math.max(12, ((event.clientY - bounds.top) / bounds.height) * 100));
    setMechanicNodes((nodes) => nodes.map((node) => node.id === id ? { ...node, x, y } : node));
  };

  const finishMechanicDrag = (event, id) => {
    dragMechanicNode(event, id);
    const board = event.currentTarget.parentElement;
    if (!board) return;
    const bounds = board.getBoundingClientRect();
    const x = Math.min(90, Math.max(10, ((event.clientX - bounds.left) / bounds.width) * 100));
    const y = Math.min(88, Math.max(12, ((event.clientY - bounds.top) / bounds.height) * 100));
    const nextNodes = mechanicNodes.map((node) => node.id === id ? { ...node, x, y } : node);
    setDraggedMechanicNode(null);
    if (!hasIntersectingWires(nextNodes)) resolveMission(mission.success);
  };

  const runTerminalCommand = (command) => {
    if (isResolving || isComplete || terminalLog.includes(command)) return;
    const nextLog = [...terminalLog, command];
    setTerminalLog(nextLog);
    if (command === 'nslookup') setMessage('Name resolution times out. Test the local workshop service directly.');
    if (command === 'local') setMessage('localhost:3000 responds. The app is healthy; inspect the DHCP lease.');
    if (command === 'lease') resolveMission(mission.success);
  };
  const applyMergeHunk = (index, source) => {
    if (isResolving || isComplete) return;
    const nextChoices = [...mergeChoices];
    nextChoices[index] = source;
    setMergeChoices(nextChoices);
    if (!nextChoices.every(Boolean)) return setMessage('Piece selected. Compare the next candidate against the expected output.');
    if (nextChoices.every((choice, choiceIndex) => choice === MERGE_HUNKS[choiceIndex].target)) return resolveMission(mission.success);
    setMessage('Output would regress. Revisit the piece that violates the stated rule.');
  };
  const handleDialogKeyDown = (event) => {
    if (event.key === 'Escape') {
      onClose();
      return;
    }
    if (event.key !== 'Tab' || !dialogRef.current) return;
    const focusable = [...dialogRef.current.querySelectorAll('button:not([disabled])')];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="intercept-backdrop" role="presentation">
      <section className="systems-intercept" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="intercept-title" onKeyDown={handleDialogKeyDown}>
        <header className="intercept-header">
          <div><span className="intercept-eyebrow">DC / PERSONNEL ACCESS</span><h2 id="intercept-title">Systems Intercept</h2></div>
          <button className="intercept-close" ref={closeRef} type="button" onClick={onClose} aria-label="Close systems intercept"><X size={17} /></button>
        </header>

        <div className="intercept-body">
          <aside className="intercept-readout">
            <span>ROUTE / {isComplete ? 'COMPLETE' : mission.code}</span>
            <strong>{isComplete ? 'SYSTEMS NOMINAL' : mission.system}</strong>
            <p>{isComplete ? 'The workshop is back online.' : mission.prompt}</p>
            <ol aria-label="Route progress">
              {INTERCEPTS.map((intercept, index) => <li key={intercept.code} className={index < stageIndex || isComplete ? 'is-cleared' : index === stageIndex ? 'is-current' : ''}><span>{intercept.code}</span>{intercept.system}</li>)}
            </ol>
          </aside>

          <div className={`intercept-stage ${isResolving ? 'is-resolving' : ''} ${isComplete ? 'is-complete' : ''}`}>
            <div className="stage-grid" aria-hidden="true" />
            <div className="stage-caption"><span>{isComplete ? 'ROUTE FINISHED' : mission.title}</span><span>{isComplete ? `${INTERCEPTS.length} / ${INTERCEPTS.length}` : `${mission.code} / ${INTERCEPTS.length}`}</span></div>
            <div className="runner-track" aria-hidden="true"><span className="intercept-runner" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/platform-assets/npcs/pixel-crawler/Rogue/Run/Run-Sheet.png)` }} /><i /><i /><i /></div>

            {!isComplete && mission.type === 'queue' && <div className="queue-board" aria-label="Build queue ordering controls">
              <div className="queue-stack">{queueOrder.map((item, index) => <div className="queue-item" key={item}><span>{String(index + 1).padStart(2, '0')}</span><b>{QUEUE_LABELS[item]}</b><div><button type="button" disabled={index === 0} onClick={() => moveQueueItem(index, -1)} aria-label={`Move ${QUEUE_LABELS[item]} earlier`}>↑</button><button type="button" disabled={index === queueOrder.length - 1} onClick={() => moveQueueItem(index, 1)} aria-label={`Move ${QUEUE_LABELS[item]} later`}>↓</button></div></div>)}</div>
              <p>ORDER THE RELEASE TRAIN / DEPLOY LAST</p>
            </div>}

            {!isComplete && mission.type === 'merge' && <div className="merge-board" aria-label="League Night merge dependency resolution">
              <div className="merge-branches" aria-hidden="true"><span>main <b>c548172</b></span><i /><span>feat/tiered-courts <b>64aca09</b></span><em>MERGE HEAD</em></div>
              <div className="merge-goal"><b>EXPECTED OUTPUT</b><span>Tiered courts keep their identifier and label, while the primary court order stays unchanged.</span></div>
              {MERGE_HUNKS.map((hunk, index) => {
                const unlocked = index === 0 || mergeChoices.slice(0, index).every(Boolean);
                const choice = mergeChoices[index];
                return <article key={hunk.file} className={`merge-hunk ${unlocked ? '' : 'is-locked'} ${choice ? `is-${choice}` : ''}`}>
                  <header><span>{String(index + 1).padStart(2, '0')}</span><b>{hunk.file}</b><em>{hunk.dependency}</em></header>
                  <div className="merge-diff">
                    <code className="merge-main">{hunk.main.map((line) => <span key={line}>− {line}</span>)}</code>
                    <div className="merge-controls"><button type="button" disabled={!unlocked} className={choice === 'main' ? 'is-selected' : ''} onClick={() => applyMergeHunk(index, 'main')} aria-label={`Discard feature change in ${hunk.file}`}>DISCARD<br /><i>→</i></button><button type="button" disabled={!unlocked} className={choice === 'branch' ? 'is-selected' : ''} onClick={() => applyMergeHunk(index, 'branch')} aria-label={`Merge feature change in ${hunk.file}`}><i>←</i><br />MERGE</button></div>
                    <code className="merge-branch">{hunk.branch.map((line) => <span key={line}>+ {line}</span>)}</code>
                  </div>
                </article>;
              })}
              <p>MERGE OR DISCARD THREE PIECES / MATCH EXPECTED OUTPUT</p>
            </div>}

            {!isComplete && mission.type === 'pipe' && <div className="pipe-board" aria-label="Cold boot conduit board">
              <div className="pipe-grid-shell">
                <div className="pipe-port pipe-port-in">IN</div>
                <div className="pipe-grid pipe-grid-medium">
                  {PIPE_TILES.map((tile, index) => <button key={index} type="button" disabled={tile.type === 'empty'} onPointerDown={(event) => { event.preventDefault(); rotatePipe(index); }} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); rotatePipe(index); } }} className={`pipe-tile pipe-${tile.type}`} aria-label={tile.type === 'empty' ? undefined : `Rotate conduit module ${index + 1}`}>
                    {tile.type !== 'empty' && <span className="pipe-shape" style={{ transform: `rotate(${pipeTurns[index] * 90}deg)` }}><i /><i /></span>}
                  </button>)}
                </div>
                <div className="pipe-port pipe-port-out">OUT</div>
              </div>
              <p>ROTATE MODULES / ESTABLISH CURRENT</p>
            </div>}

            {!isComplete && mission.type === 'mechanic' && <div className="mechanic-board" aria-label="Module untangle puzzle">
              <div className="mechanic-status"><span className="signal-dot" /> {intersectingWireIndexes(mechanicNodes).size} CROSSED WIRES <b>DRAG TO CLEAR</b></div>
              <div className="untangle-arena"><span className="arena-label">MODULE BUS / LIVE TOPOLOGY</span><svg className="mechanic-wires" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">{MECHANIC_EDGES.map(([from, to], index) => { const start = mechanicNodes.find((node) => node.id === from); const end = mechanicNodes.find((node) => node.id === to); const linked = draggedMechanicNode && (from === draggedMechanicNode || to === draggedMechanicNode); const crossed = intersectingWireIndexes(mechanicNodes).has(index); return <line key={`${from}-${to}`} x1={start.x} y1={start.y} x2={end.x} y2={end.y} className={`${crossed ? 'is-crossed' : ''} ${linked ? 'is-linked' : ''}`} />; })}</svg>{mechanicNodes.map((node) => <button key={node.id} type="button" className={`mechanic-node ${draggedMechanicNode === node.id ? 'is-dragged' : ''}`} style={{ '--node-x': `${node.x}%`, '--node-y': `${node.y}%` }} onPointerDown={(event) => { setDraggedMechanicNode(node.id); event.currentTarget.setPointerCapture(event.pointerId); }} onPointerMove={(event) => { if (event.currentTarget.hasPointerCapture(event.pointerId)) dragMechanicNode(event, node.id); }} onPointerUp={(event) => { finishMechanicDrag(event, node.id); event.currentTarget.releasePointerCapture(event.pointerId); }}>{node.label}</button>)}</div>
              <p>DRAG MODULES / REMOVE EVERY WIRE INTERSECTION</p>
            </div>}

            {!isComplete && mission.type === 'terminal' && <div className="terminal-board" aria-label="DNS diagnostics terminal">
              <div className="terminal-window"><div className="terminal-log"><span>workshop@guest:~$ connect wifi</span><span className="terminal-ok">connected: greenhouse-guest / 10.24.8.19</span>{terminalLog.map((command) => <React.Fragment key={command}><span>workshop@guest:~$ {command === 'lease' ? 'cat /var/lib/dhcp/lease' : command === 'local' ? 'curl http://localhost:3000' : 'nslookup portfolio.local'}</span><span className={command === 'nslookup' ? 'terminal-error' : 'terminal-ok'}>{command === 'nslookup' ? ';; no servers could be reached' : command === 'local' ? 'HTTP 200 / workshop service healthy' : 'option domain-name-servers: (not present)'}</span></React.Fragment>)}</div></div>
              <div className="terminal-actions"><button type="button" disabled={terminalLog.includes('nslookup')} onClick={() => runTerminalCommand('nslookup')}>nslookup portfolio.local</button><button type="button" disabled={!terminalLog.includes('nslookup') || terminalLog.includes('local')} onClick={() => runTerminalCommand('local')}>curl localhost:3000</button><button type="button" disabled={!terminalLog.includes('local') || terminalLog.includes('lease')} onClick={() => runTerminalCommand('lease')}>inspect DHCP lease</button></div>
              <p>COLLECT EVIDENCE / IDENTIFY ROOT CAUSE / RESTORE DNS</p>
            </div>}

            {isComplete && <div className="completion-mark"><span>✓</span><div><b>EXIT ROUTE<br />ESTABLISHED</b>{recoveredProject && <button type="button" onClick={() => { onRecoverProject(recoveredProject); onClose(); }}>RECOVER CASE FILE / {recoveredProject.title}</button>}</div></div>}
          </div>
        </div>

        <footer className="intercept-footer">
          <p aria-live="polite"><span className="signal-dot" /> {message}</p>
          <div><button className="intercept-reset" type="button" onClick={reset}><RotateCcw size={14} /> Reset route</button><span>ESC / CLOSE</span></div>
        </footer>
      </section>
    </div>
  );
}

function Schematic() {
  return (
    <div className="commit-tree" aria-hidden="true">
      <span className="commit-tree-label">COMMIT LOG</span>
      <svg viewBox="0 0 132 188" fill="none" role="img">
        <path className="commit-trunk" d="M35 22V145" />
        <path className="commit-branch" d="M35 136L83 108V78L35 54" />
        <circle className="commit-node commit-head" cx="35" cy="22" r="4" />
        <circle className="commit-node commit-merge" cx="35" cy="54" r="4" /><circle className="commit-node" cx="35" cy="136" r="3" />
        <circle className="commit-node" cx="83" cy="108" r="3" /><circle className="commit-node" cx="83" cy="78" r="3" />
        <path className="commit-tick" d="M20 22H50M20 54H50M20 136H50M68 78H98M68 108H98" />
        <text x="51" y="25">a746b0c</text><text x="51" y="57">11ad50f</text><text x="51" y="139">c37a055</text><text x="91" y="81">ef483c2</text><text x="91" y="111">eaa8da8</text>
      </svg>
      <div className="commit-tree-ref"><span>●</span> main</div>
      <small>11ad50f / MERGE</small>
    </div>
  );
}

function ProjectRow({ project, index, active, onSelect }) {
  return (
    <button
      className={`project-row ${active ? 'is-active' : ''}`}
      onClick={() => onSelect(project)}
      aria-current={active ? 'true' : undefined}
    >
      <span className="project-row-number">{pad(index)}</span>
      <img src={`${process.env.PUBLIC_URL}/projectImages/${project.image}?v=${project.imageVersion || 1}`} alt="" loading="lazy" />
      <span className="project-row-title">{project.title}</span>
      <span className="project-row-type">{project.category === 'work' ? 'WORK' : 'BUILD'}</span>
      <ArrowUpRight className="project-row-arrow" size={15} />
    </button>
  );
}

function ProjectDossier({ project, index, total }) {
  const projectLinkText = project.caseStudyButtonText || (project.category === 'work' ? 'View reference' : 'Open project');

  return (
    <article className="dossier" id="current-project">
      <div className="dossier-media">
        <div className="media-meta"><span>CASE FILE / {pad(index)}</span><span>{project.category === 'work' ? 'SELECTED WORK' : 'INDEPENDENT BUILD'}</span></div>
        <img src={`${process.env.PUBLIC_URL}/projectImages/${project.image}?v=${project.imageVersion || 1}`} alt={project.title} />
        <div className="media-corner">{pad(index)} / {String(total).padStart(2, '0')}</div>
      </div>

      <div className="dossier-brief">
        <div className="dossier-kicker"><span className="signal-dot" /> CURRENT PROJECT</div>
        <p className="dossier-category">{project.category === 'work' ? 'Product & engineering delivery' : 'Product experiment'}</p>
        <h1>{project.title}</h1>
        <dl>
          <div><dt>ROLE</dt><dd>{project.category === 'work' ? 'Product / engineering leadership' : 'Creator / builder'}</dd></div>
          <div><dt>OUTCOME</dt><dd>{project.description}</dd></div>
          <div><dt>FOCUS</dt><dd>{project.skills.slice(0, 3).join(' · ')}</dd></div>
        </dl>
        {project.fieldNote && <p className="dossier-field-note"><span>FIELD NOTE</span>{project.fieldNote}</p>}
        <div className="dossier-actions">
          {project.website && <a href={project.website} className="solid-action">Website <ExternalLink size={14} /></a>}
          {project.caseStudyLink && <a href={project.caseStudyLink} target="_blank" rel="noreferrer" className="solid-action">{projectLinkText} <ExternalLink size={14} /></a>}
          {project.youtubeLink && <a href={project.youtubeLink} target="_blank" rel="noreferrer" className="quiet-action"><Play size={13} fill="currentColor" /> Watch overview</a>}
        </div>
      </div>
    </article>
  );
}

export default function App() {
  const orderedProjects = useMemo(() => {
    return [...projectsData].sort((left, right) => left.positionInList - right.positionInList);
  }, []);
  const [activeProject, setActiveProject] = useState(
    orderedProjects.find((project) => project.featured) || orderedProjects[0],
  );
  const [filter, setFilter] = useState('all');
  const [isInterceptOpen, setIsInterceptOpen] = useState(false);
  const [isPikachuRunning, setIsPikachuRunning] = useState(false);
  const diagnosticsMode = new URLSearchParams(window.location.search).has('diagnostics');

  const indexedProjects = useMemo(() => orderedProjects.filter((project) => (
    filter === 'all' || project.category === filter
  )), [filter, orderedProjects]);
  const activeIndex = orderedProjects.findIndex((project) => project.title === activeProject.title);

  const selectProject = (project) => {
    setActiveProject(project);
  };

  const sendPikachuRunning = () => {
    if (!isPikachuRunning) setIsPikachuRunning(true);
  };

  return (
    <div className="workshop-shell">
      <aside className="command-rail">
        <a className="identity" href="#top" aria-label="Return to top">
          <span>{PROFILE.name}</span>
        </a>
        <p className="rail-role">{PROFILE.role}</p>

        <div className="availability"><span className="signal-dot" /> <span>{PROFILE.status}</span></div>

        <div className="rail-index" aria-label="Project filters">
          <div className="rail-heading"><span>WORKSHOP INDEX</span><span>{String(orderedProjects.length).padStart(2, '0')}</span></div>
          <div className="filter-switches">
            <button className={filter === 'all' ? 'is-selected' : ''} onClick={() => setFilter('all')}>All systems</button>
            <button className={filter === 'work' ? 'is-selected' : ''} onClick={() => setFilter('work')}>Professional work</button>
            <button className={filter === 'personal' ? 'is-selected' : ''} onClick={() => setFilter('personal')}>Independent builds</button>
          </div>
          <div className="rail-project-list">
            {indexedProjects.map((project) => <button key={project.title} className={project.title === activeProject.title ? 'is-active' : ''} onClick={() => selectProject(project)}><span>{pad(orderedProjects.findIndex((item) => item.title === project.title))}</span>{project.title}</button>)}
          </div>
        </div>

        <div className="rail-links">
          <a href={`${process.env.PUBLIC_URL}/Dan_Chernopolskii_EM_PM.pdf`} target="_blank" rel="noreferrer"><span aria-hidden="true">CV</span> Resume / PDF <ArrowUpRight size={13} /></a>
          <a href="https://www.linkedin.com/in/dan-chernopolskii/" target="_blank" rel="noreferrer"><Linkedin size={14} /> LinkedIn <ArrowUpRight size={13} /></a>
          <a href="https://github.com/dchernopolskii" target="_blank" rel="noreferrer"><Github size={14} /> GitHub <ArrowUpRight size={13} /></a>
        </div>
        <button className="rail-avatar" type="button" onClick={() => setIsInterceptOpen(true)} aria-label="Open optional personnel access" data-tooltip="Personnel access">
          <span aria-hidden="true" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/platform-assets/npcs/pixel-crawler/Rogue/Idle/Idle-Sheet.png)` }} />
        </button>
        <div className="rail-footer"><span>v.26.07</span><span>SYS.OK</span></div>
      </aside>

      <main id="top" className="workbench">
        <header className="workbench-header">
          <span>DC / WORKSHOP INDEX</span>
          <span className="header-focus">{PROFILE.focus}</span>
          <nav aria-label="Primary navigation"><a href="#work">Work</a><a href="#contact">Contact</a></nav>
        </header>
        {diagnosticsMode && <div className="diagnostic-strip" role="status"><span>DIAGNOSTICS / ENABLED</span><span>{String(orderedProjects.length).padStart(2, '0')} CASE FILES</span><span>{orderedProjects.filter((project) => project.category === 'work').length} WORK / {orderedProjects.filter((project) => project.category === 'personal').length} BUILDS</span><span>SYS.OK</span></div>}

        <section className="hero-frame" aria-label="Selected project">
          <div className="hero-margin">
            <div><span className="orange-text">{pad(activeIndex)} / CURRENT</span><p>Selected work, systems, products and useful experiments.</p></div>
            <Schematic />
          </div>
          <ProjectDossier project={activeProject} index={activeIndex} total={orderedProjects.length} />
        </section>

        <section id="work" className="project-archive" aria-labelledby="archive-title">
          <div className="archive-heading"><span id="archive-title">PROJECT ARCHIVE</span><span>SELECT A CASE FILE / {String(orderedProjects.length).padStart(2, '0')} TOTAL</span></div>
          <div className="archive-list">
            {orderedProjects.map((project, index) => <ProjectRow key={project.title} project={project} index={index} active={activeProject.title === project.title} onSelect={selectProject} />)}
          </div>
        </section>

        <section id="about" className="field-notes">
          <div className="notes-label">NOTES / 01</div>
          <div><h2>Build tools that work in the real world.</h2><p>I lead product and engineering work where hardware, software, and the people using them have to fit together. I’m especially drawn to tools that improve access, independence, or everyday work—whether that’s an adaptive controller, a connected machine, or a quietly useful app.</p></div>
          <div className="notes-meta"><span>ENVIRONMENT</span><p>Hardware · software · real-world use · teams</p><span>INTERESTS</span><p>Accessible tech · robotics · photography · practical AI</p></div>
        </section>

        <section id="contact" className="contact-portal">
          <div><span className="contact-label">OPEN COMMS</span><h2>Have something worth building?</h2><p>I’m drawn to products at the hardware–software edge, especially ones that improve access, independence, or everyday work. If that sounds like what you’re making, say hello.</p></div>
          <ContactForm />
        </section>

        <footer className="workshop-footer"><span>© {new Date().getFullYear()} {PROFILE.name}</span><span>Designed as a working index.</span></footer>
      </main>
      <button className="pikachu-nest" type="button" onClick={sendPikachuRunning} aria-label="Send Pikachu on a lap" data-tooltip="?">
        <img src={`${process.env.PUBLIC_URL}/easter-eggs/pikachu-nest.png`} alt="" aria-hidden="true" />
      </button>
      {isPikachuRunning && <img className="pikachu-runner" src={`${process.env.PUBLIC_URL}/easter-eggs/pikachu-run.gif`} alt="" aria-hidden="true" onAnimationEnd={() => setIsPikachuRunning(false)} />}
      {isInterceptOpen && <SystemsIntercept onClose={() => setIsInterceptOpen(false)} projects={orderedProjects} onRecoverProject={selectProject} />}
    </div>
  );
}
